/**
 * A brand's SERVICE day opens at 5:00 AM and may run to 2:00 AM the next calendar day, so a store
 * open "Wednesday 11 AM to 2 AM" holds one Wednesday window in `openHours`. The labels after
 * midnight are stored under Wednesday but their real timestamps fall on Thursday.
 *
 * That makes 5:00 AM the dividing line: a label below it can only belong to the *previous* service
 * day. 5 AM specifically, because on both DST transition nights a post-midnight slot's resolved
 * wall clock still reads under 5 — so a label's service-day classification and a booking's
 * service-day attribution always agree. At a midnight or 2 AM boundary they would disagree inside
 * the spring-forward gap.
 */
export const SERVICE_DAY_START_MINUTES = 5 * 60;

/** Minutes since midnight for an "hh:mm AM/PM" label; NaN if it cannot be read. */
export const labelMinutes = (label: string): number => {
  const match = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec(label.trim());
  if (!match) return NaN;
  const hour12 = Number(match[1]) % 12;
  const hour = /pm/i.test(match[3]) ? hour12 + 12 : hour12;
  return hour * 60 + Number(match[2]);
};

/** True for a label that belongs to the previous service day's late-night run. */
export const isLateNightLabel = (label: string): boolean =>
  labelMinutes(label) < SERVICE_DAY_START_MINUTES;

export interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

const pad2 = (value: number): string => value.toString().padStart(2, '0');

export const getCalendarDatePartsInTimezone = (
  date: Date,
  timezone: string
): CalendarDateParts => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '0';

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
  };
};

export const getWeekdayForCalendarDay = (
  year: number,
  month: number,
  day: number,
  timezone: string
): string => {
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toLocaleDateString('en-US', {
    weekday: 'long',
    timeZone: timezone,
  });
};

export const toCalendarOrdinal = (year: number, month: number, day: number): number =>
  year * 10_000 + month * 100 + day;

export const addCalendarDays = (
  year: number,
  month: number,
  day: number,
  days: number
): CalendarDateParts => {
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth() + 1,
    day: next.getUTCDate(),
  };
};

export const calendarDayToUtcMidnightKey = (
  year: number,
  month: number,
  day: number
): string => new Date(Date.UTC(year, month - 1, day)).toISOString();

/** Map key -> local-midnight Date for calendar pickers (same Y-M-D label as the key). */
export const scheduleSlotKeyToPickerDate = (dateKey: string): Date => {
  const { year, month, day } = parseUtcMidnightKeyToCalendarParts(dateKey);
  return new Date(year, month - 1, day);
};

/** Calendar picker selection -> map key used by slot generation. */
export const pickerDateToScheduleSlotKey = (date: Date): string =>
  calendarDayToUtcMidnightKey(date.getFullYear(), date.getMonth() + 1, date.getDate());

export const parseUtcMidnightKeyToCalendarParts = (dateKey: string): CalendarDateParts => {
  const date = new Date(dateKey);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

const getWallClockPartsInTimezone = (
  date: Date,
  timezone: string
): CalendarDateParts & { hours: number; minutes: number } => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '0';

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hours: Number(get('hour')),
    minutes: Number(get('minute')),
  };
};

export const parseCalendarDateTimeToUtc = (
  year: number,
  month: number,
  day: number,
  timeString: string,
  timezone: string
): Date => {
  const [time, modifier] = timeString.split(' ');
  let [hours, minutes] = time.split(':').map(Number);

  if (modifier === 'PM' && hours < 12) hours += 12;
  else if (modifier === 'AM' && hours === 12) hours = 0;

  let guess = Date.UTC(year, month - 1, day, hours, minutes, 0);

  for (let attempt = 0; attempt < 5; attempt++) {
    const wallClock = getWallClockPartsInTimezone(new Date(guess), timezone);
    const desired = Date.UTC(year, month - 1, day, hours, minutes, 0);
    const actual = Date.UTC(
      wallClock.year,
      wallClock.month - 1,
      wallClock.day,
      wallClock.hours,
      wallClock.minutes,
      0
    );
    const delta = desired - actual;
    if (delta === 0) {
      return new Date(guess);
    }
    guess += delta;
  }

  return new Date(guess);
};
